import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

const ADMIN_USER_ID =
  "6dd6fb2d-8b2f-41f3-9cc7-490c968fc903";

function AdminLogin() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] =
    useState(true);

  const [message, setMessage] = useState("");

  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (
          session?.user &&
          session.user.id === ADMIN_USER_ID
        ) {
          navigate("/admin/bookings", {
            replace: true,
          });

          return;
        }

        if (
          session?.user &&
          session.user.id !== ADMIN_USER_ID
        ) {
          await supabase.auth.signOut();
        }
      } catch (error) {
        console.error(
          "Admin session check error:",
          error
        );
      } finally {
        setCheckingSession(false);
      }
    };

    checkExistingSession();
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setMessage("");
    setLoading(true);

    try {
      const {
        data,
        error,
      } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        throw error;
      }

      if (!data?.user) {
        throw new Error(
          "Could not verify the admin account."
        );
      }

      if (data.user.id !== ADMIN_USER_ID) {
        await supabase.auth.signOut();

        setMessage(
          "This account does not have administrator access."
        );

        return;
      }

      navigate("/admin/bookings", {
        replace: true,
      });
    } catch (error) {
      console.error("Admin login error:", error);

      if (
        error?.message
          ?.toLowerCase()
          .includes("invalid login credentials")
      ) {
        setMessage(
          "Incorrect email or password."
        );
      } else {
        setMessage(
          error?.message ||
            "Unable to sign in. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div style={styles.loadingPage}>
        <div style={styles.loadingText}>
          Checking admin session...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.backgroundGlowOne} />
      <div style={styles.backgroundGlowTwo} />

      <div style={styles.wrapper}>
        <div style={styles.brandSection}>
          <div style={styles.logo}>
            AR
          </div>

          <div>
            <div style={styles.brandTitle}>
              AutoRent Albania
            </div>

            <div style={styles.brandSubtitle}>
              Administration
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.smallLabel}>
              ADMIN PANEL
            </div>

            <h1 style={styles.title}>
              Welcome back
            </h1>

            <p style={styles.description}>
              Sign in to manage reservations,
              availability and your vehicle fleet.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            style={styles.form}
          >
            <div style={styles.fieldGroup}>
              <label
                htmlFor="admin-email"
                style={styles.label}
              >
                Email
              </label>

              <input
                id="admin-email"
                type="email"
                value={email}
                onChange={(event) =>
                  setEmail(event.target.value)
                }
                placeholder="admin@example.com"
                autoComplete="email"
                required
                disabled={loading}
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label
                htmlFor="admin-password"
                style={styles.label}
              >
                Password
              </label>

              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(event) =>
                  setPassword(event.target.value)
                }
                placeholder="Enter your password"
                autoComplete="current-password"
                required
                disabled={loading}
                style={styles.input}
              />
            </div>

            {message && (
              <div style={styles.errorBox}>
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.loginButton,
                ...(loading
                  ? styles.loginButtonDisabled
                  : {}),
              }}
            >
              {loading
                ? "Signing in..."
                : "Sign In"}
            </button>
          </form>

          <div style={styles.securityBox}>
            <div style={styles.securityIcon}>
              <svg
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect
                  x="5"
                  y="10"
                  width="14"
                  height="10"
                  rx="2"
                />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
              </svg>
            </div>

            <div>
              <div style={styles.securityTitle}>
                Secure administration
              </div>

              <div style={styles.securityText}>
                Access is restricted to the
                authorized AutoRent Albania
                administrator.
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => navigate("/")}
          style={styles.returnButton}
        >
          ← Return to website
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(145deg, #0a0c0f 0%, #11151b 55%, #0c0f13 100%)",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 18px",
    position: "relative",
    overflow: "hidden",
  },

  loadingPage: {
    minHeight: "100vh",
    background: "#0d0f12",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
  },

  loadingText: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#a2a7ae",
  },

  backgroundGlowOne: {
    position: "absolute",
    width: "420px",
    height: "420px",
    borderRadius: "50%",
    background:
      "rgba(255,255,255,0.035)",
    top: "-180px",
    right: "-130px",
    filter: "blur(10px)",
  },

  backgroundGlowTwo: {
    position: "absolute",
    width: "340px",
    height: "340px",
    borderRadius: "50%",
    background:
      "rgba(255,255,255,0.025)",
    bottom: "-180px",
    left: "-100px",
    filter: "blur(10px)",
  },

  wrapper: {
    width: "100%",
    maxWidth: "460px",
    position: "relative",
    zIndex: 2,
  },

  brandSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    marginBottom: "28px",
  },

  logo: {
    width: "48px",
    height: "48px",
    borderRadius: "14px",
    background: "#ffffff",
    color: "#111317",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: "900",
  },

  brandTitle: {
    fontSize: "16px",
    lineHeight: "1.1",
    fontWeight: "900",
  },

  brandSubtitle: {
    marginTop: "5px",
    color: "#7f858d",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "1.4px",
    fontWeight: "800",
  },

  card: {
    width: "100%",
    padding: "38px",
    borderRadius: "26px",
    background:
      "rgba(255,255,255,0.055)",
    border:
      "1px solid rgba(255,255,255,0.10)",
    boxShadow:
      "0 30px 80px rgba(0,0,0,0.35)",
    backdropFilter: "blur(16px)",
  },

  cardHeader: {
    marginBottom: "30px",
  },

  smallLabel: {
    color: "#858b93",
    fontSize: "10px",
    fontWeight: "900",
    letterSpacing: "1.8px",
    marginBottom: "13px",
  },

  title: {
    margin: 0,
    color: "#ffffff",
    fontSize: "32px",
    lineHeight: "1.1",
    letterSpacing: "-1px",
  },

  description: {
    margin: "14px 0 0",
    color: "#9298a0",
    fontSize: "14px",
    lineHeight: "1.7",
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "19px",
  },

  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  label: {
    fontSize: "12px",
    fontWeight: "800",
    color: "#d1d4d8",
  },

  input: {
    width: "100%",
    height: "52px",
    borderRadius: "14px",
    border:
      "1px solid rgba(255,255,255,0.10)",
    outline: "none",
    padding: "0 16px",
    background:
      "rgba(0,0,0,0.18)",
    color: "#ffffff",
    fontSize: "14px",
  },

  errorBox: {
    padding: "13px 15px",
    borderRadius: "12px",
    background:
      "rgba(220, 70, 70, 0.12)",
    border:
      "1px solid rgba(220, 70, 70, 0.24)",
    color: "#ffb6b6",
    fontSize: "13px",
    lineHeight: "1.5",
  },

  loginButton: {
    width: "100%",
    height: "54px",
    marginTop: "4px",
    border: "none",
    borderRadius: "14px",
    background: "#ffffff",
    color: "#111317",
    fontSize: "14px",
    fontWeight: "900",
    cursor: "pointer",
    transition: "0.2s ease",
  },

  loginButtonDisabled: {
    opacity: 0.65,
    cursor: "not-allowed",
  },

  securityBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    marginTop: "28px",
    paddingTop: "23px",
    borderTop:
      "1px solid rgba(255,255,255,0.08)",
  },

  securityIcon: {
    width: "35px",
    height: "35px",
    flexShrink: 0,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "rgba(255,255,255,0.07)",
    color: "#ffffff",
  },

  securityTitle: {
    color: "#d8dadd",
    fontSize: "12px",
    fontWeight: "800",
    marginBottom: "4px",
  },

  securityText: {
    color: "#777d85",
    fontSize: "11px",
    lineHeight: "1.5",
  },

  returnButton: {
    display: "block",
    margin: "24px auto 0",
    padding: "8px 12px",
    border: "none",
    background: "transparent",
    color: "#868c94",
    fontSize: "12px",
    fontWeight: "700",
    cursor: "pointer",
  },
};

export default AdminLogin;